import { ReactElement, useState } from 'react';

import ConfirmModal from '~/ui/components/common/ConfirmModal';
import ListItem from '~/ui/components/common/ListItem';
import ProfilePhotoUpload from '~/ui/components/common/ProfilePhotoUpload';
import getRiskAssessmentLabel from '~/ui/pages/MedicationLogistics/helpers/getRiskAssessmentLabel';
import ContactDetails from '../ContactDetails';
import DischargeDetails from '../DischargeDetails';
import EditableListItem from '../EditableListItem';
import LocationDetails from '../LocationDetails';
import Memos from '../Memos';
import Status from '../Status';
import TeamsDetails from '../TeamsDetails';

import useClientMultiTeamId from '~/hooks/useClientMultiTeamId';
import api from '~/services/api';
import { IClientDetailedV3 } from '~/services/api/client/types';
import { useStoreActions } from '~/store/hooks';
import { suicideRiskColor } from '~/ui/constants/suicideRisks';
import { isoToLocal } from '~/utils/date/date';
import { extractErrorMessage } from '~/utils/error/error';
import { isFile } from '~/utils/file';
import { getExpiresIn, getPaperworkStatus, getRecertificationColorByDate } from './helpers';

import styles from './PersonalDetails.module.scss';

interface IProps {
  client: IClientDetailedV3;
  clinicId?: string;
  actTeamId?: string;
}

const PersonalDetails = ({ client, clinicId, actTeamId }: IProps): ReactElement => {
  const [showConfirm, setShowConfirm] = useState(false);
  const [value, setValue] = useState(client.photo || null);
  const [error, setError] = useState(null);
  const [loadingPhoto, setLoadingPhoto] = useState(false);

  const { showError, showNotify } = useStoreActions(actions => actions.snackbar);
  const { onUpdateClientNames } = useStoreActions(actions => actions.client);

  const { suicideRisk, riskAssessment } = client;

  const { getClientMultiTeamId } = useClientMultiTeamId();
  const clientTeamId = getClientMultiTeamId(client?.actTeams);

  const teamId = actTeamId || String(clientTeamId);

  const formattedBirth = isoToLocal(client.dateOfBirth);

  const formattedRecertification = client.medicaIdRecertificationDate
    ? isoToLocal(client.medicaIdRecertificationDate, 'MM/yyyy')
    : null;

  const color = suicideRiskColor[suicideRisk];

  const requestInfo = { clinicId, clientId: String(client.id), teamId };

  const onUpload = async (file: any) => {
    try {
      setLoadingPhoto(true);

      if (!isFile(file)) return;

      const formData = new FormData();
      formData.append('file', file);
      const { fileName } = await api.file.uploadFile(formData).then(r => r.data);

      await api.client.uploadClientPhoto(requestInfo, fileName);

      setValue(file);
      showNotify({ message: 'Photo successfully updated' });
    } catch (e) {
      showError(extractErrorMessage(e));
    } finally {
      setLoadingPhoto(false);
    }
  };

  const onRemove = async () => {
    try {
      setLoadingPhoto(true);

      await api.client.uploadClientPhoto(requestInfo, '');

      setValue('');
      setShowConfirm(false);
      showNotify({ message: 'Photo successfully removed!' });
    } catch (e) {
      showError(extractErrorMessage(e));
    } finally {
      setLoadingPhoto(false);
    }
  };

  const onSaveClientNames = async ({
    preferredName = client?.preferredName || '',
    personalPronouns = client?.personalPronouns || '',
  }) => {
    try {
      await onUpdateClientNames({
        requestInfo,
        requestPayload: { preferredName, personalPronouns },
      });
    } catch (e) {
      showError(extractErrorMessage(e));
    }
  };

  const description = 'Are you sure you want to remove client photo?';

  const archivedTeams = client.actTeams.filter(team => team.isArchived);

  return (
    <div className={styles.content}>
      <div className={styles.row}>
        <div className={styles.column}>
          <div className={styles.listItemBlock}>
            <div className={styles.item}>
              <h4>Client Profile Details</h4>
              <h4 style={{ color }}>
                {!!riskAssessment && (
                  <span className={styles.black}>{getRiskAssessmentLabel(riskAssessment)}/</span>
                )}
                SR{client.aot ? <span className={styles.black}>/AOT</span> : ''}
              </h4>
            </div>
            <ListItem label="First Name" content={client.firstName} moreLabelWidth />
            <ListItem label="Last Name" content={client.lastName} moreLabelWidth />
            <EditableListItem
              label="Preferred Name"
              content={client.preferredName}
              onSave={onSaveClientNames}
              disabled={client.isArchived}
            />
            <EditableListItem
              label="Personal Pronouns"
              content={client.personalPronouns}
              onSave={onSaveClientNames}
              disabled={client.isArchived}
            />
            <ListItem label="Gender" content={client.gender.name} moreLabelWidth />
            {!!client.genderIdentity?.name && (
              <ListItem
                label="Gender Identity"
                content={client.genderIdentity.name}
                moreLabelWidth
              />
            )}
            <ListItem label="Date of Birth" content={formattedBirth} moreLabelWidth />
            {client.race && <ListItem label="Race" content={client.race.name} moreLabelWidth />}
            <ListItem
              label="Medicaid #"
              content={
                <div
                  style={{
                    color: getRecertificationColorByDate(
                      client.medicaIdRecertificationDate,
                      client.paperworkStatus,
                    ),
                  }}
                >
                  {client.medicaId}
                </div>
              }
              moreLabelWidth
            />
            <ListItem label="EMR MRN" content={client.emrMrn} moreLabelWidth />
            {!!client.educationLevel?.name && (
              <ListItem
                label="Education Level"
                content={client.educationLevel.name}
                moreLabelWidth
              />
            )}
            {!!formattedRecertification && (
              <ListItem
                label="Recertification Expiration Date"
                content={
                  <div
                    style={{
                      color: getRecertificationColorByDate(
                        client.medicaIdRecertificationDate,
                        client.paperworkStatus,
                      ),
                    }}
                  >
                    {formattedRecertification}
                    {getExpiresIn(client.medicaIdRecertificationDate)}
                    {!!client.paperworkStatus && (
                      <>
                        <br />
                        <span className={styles.paperwork}>
                          {getPaperworkStatus(client.paperworkStatus)}
                        </span>
                      </>
                    )}
                  </div>
                }
                moreLabelWidth
              />
            )}
          </div>
        </div>
        <div className={styles.rightColumn}>
          <div className={styles.uploaderWrapper}>
            <ProfilePhotoUpload
              disabled={loadingPhoto || client.isArchived}
              value={value}
              onUpload={onUpload}
              onError={setError}
              onRemove={() => setShowConfirm(true)}
              showButtons={!client.isArchived}
            />
            {error && <div className={styles.red}>{error}</div>}
          </div>
        </div>
        {showConfirm && (
          <ConfirmModal
            description={description}
            confirmText="Remove"
            onClose={() => setShowConfirm(false)}
            onConfirm={() => onRemove()}
          />
        )}
      </div>
      <div className={styles.row}>
        <div className={styles.column}>
          <TeamsDetails client={client} clinicId={clinicId} actTeamId={teamId} />
          <LocationDetails client={client} clinicId={clinicId} actTeamId={teamId} />
          <ContactDetails client={client} clinicId={clinicId} actTeamId={teamId} />
        </div>
        <div className={styles.rightColumn}>
          <Status client={client} clinicId={clinicId} actTeamId={teamId} />
          <Memos client={client} clinicId={clinicId} actTeamId={teamId} />
          {(client.isArchived || archivedTeams.length > 0) && (
            <DischargeDetails clientTeams={archivedTeams} isArchivedClient={client.isArchived} />
          )}
        </div>
      </div>
    </div>
  );
};

export default PersonalDetails;
